import {
  Check,
  Crosshair,
  Lightbulb,
  Maximize2,
  MoreHorizontal,
  Redo2,
  Save,
  Search,
  Star,
  Undo2,
} from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack, type JSX } from 'solid-js';
import { GradientEditor } from '../components/GradientEditor';
import { RoomCanvas, type RoomCanvasHandle } from '../components/RoomCanvas';
import { BusyButton, BusySlot, CardSection, ConfirmModal, EmptyState, InlineNotice, Modal, ScreenHeader } from '../components/ui';
import {
  CLOSED_GRADIENT,
  gradientApplied,
  gradientEntryLabel,
  gradientSelectionId,
  gradientSessionForSelection,
  toggleGradientSession,
  type GradientSession,
} from '../lib/gradientSession';
import { sceneAppliedNotice, sceneApplyTakeoverAction, sceneCardCopy } from '../lib/sceneCardCopy';
import { groupScenesByFavorite } from '../lib/sceneFavorites';
import { decodeSceneLevels } from '../lib/sceneLevels';
import { rigRecordId } from '../lib/records';
import {
  GRADIENT_PRESETS,
  clampLevel,
  fixtureIntensityKeys,
  gradientLevels,
  groupElementLevelBatches,
  litBlock,
  liveElementLevel,
  mapLevelsToSelection,
  runDistributions,
  selectionLevels,
  selectionSummary,
  summarizeLevels,
  toElementTargets,
  type GradientStop,
  type PanelModel,
} from '../lib/panelElements';
import { clearPanelHistory, panelHistoryFor } from '../lib/panelHistory';
import {
  applyRunClick,
  selectionForSceneSave,
  type RunClickModifiers,
  type RunClickTarget,
} from '../lib/panelSelection';
import type { SceneSummary } from '../lib/types';
import { useVenue, type RoomConfirmation } from '../state/VenueProvider';
import { TEST_IDS } from '../testIds';

function mapEquals(left: ReadonlyMap<string, number>, right: ReadonlyMap<string, number>): boolean {
  if (left.size !== right.size) return false;
  for (const [key, value] of left) if (Math.abs(value - (right.get(key) ?? Number.NaN)) > .000_001) return false;
  return true;
}

/**
 * The pixels a change moved, as a history entry's two directions.
 *
 * A pixel with no known level on a side has nothing to record there: a missing "before" is a pixel whose
 * fixture the room had not reported, and recording it as 0 is how Undo once wrote a level the room never
 * had. Such a pixel goes into `after` alone, so Redo can repeat the change and Undo leaves it alone.
 */
function changedLevels(before: ReadonlyMap<string, number>, after: ReadonlyMap<string, number>): {
  before: Map<string, number>;
  after: Map<string, number>;
} {
  const changedBefore = new Map<string, number>();
  const changedAfter = new Map<string, number>();
  for (const [key, level] of after) {
    const newLevel = clampLevel(level);
    if (!before.has(key)) {
      changedAfter.set(key, newLevel);
      continue;
    }
    const oldLevel = clampLevel(before.get(key)!);
    if (Math.abs(oldLevel - newLevel) <= .000_001) continue;
    changedBefore.set(key, oldLevel);
    changedAfter.set(key, newLevel);
  }
  return { before: changedBefore, after: changedAfter };
}

function sceneId(scene: SceneSummary): string {
  return scene.sceneId || scene.id || '';
}

/**
 * How close two levels have to be to count as the same level.
 *
 * One byte of output quantization is about 0.0039, so this is "within a byte on the wire". Anything
 * wider erases real one-percent steps.
 */
const LEVEL_TOLERANCE = .004;

export function PanelScreen(): JSX.Element {
  const venue = useVenue();
  const legacySceneCommands = createMemo(() => !venue.recordRigSelected() || venue.piLineSelected());
  const sceneEditEnabled = createMemo(() => venue.controlAvailable());
  const layoutLoading = venue.roomLayoutLoading;
  const layoutWarning = venue.roomLayoutWarning;
  const [selectedKeys, setSelectedKeys] = createSignal<Set<string>>(new Set());
  const [selectionAnchor, setSelectionAnchor] = createSignal<string>();
  const [draftLevels, setDraftLevels] = createSignal<Map<string, number>>(new Map());
  const [sceneModal, setSceneModal] = createSignal(false);
  const [sceneName, setSceneName] = createSignal('');
  const [sceneQuery, setSceneQuery] = createSignal('');
  const [sceneMenuId, setSceneMenuId] = createSignal('');
  const [renamingSceneId, setRenamingSceneId] = createSignal('');
  const [renameSceneName, setRenameSceneName] = createSignal('');
  const [deletingScene, setDeletingScene] = createSignal<SceneSummary>();
  const [gradient, setGradient] = createSignal<GradientSession>(CLOSED_GRADIENT);
  const [gradientStops, setGradientStops] = createSignal<GradientStop[]>(GRADIENT_PRESETS['fade-in'].map((stop) => ({ ...stop })));
  const [gradientMirror, setGradientMirror] = createSignal(false);
  const [historyVersion, setHistoryVersion] = createSignal(0);
  const [runsOpen, setRunsOpen] = createSignal(false);
  let history = panelHistoryFor(roomKey());
  let absoluteBefore: Map<string, number> | undefined;
  let gradientBefore: Map<string, number> | undefined;
  let levelDispatchTail: Promise<unknown> = Promise.resolve();
  let previousHistoryIdentity = '';
  /** The confirmed levels this screen has already reconciled its overlay against. */
  let previousAuthoritativeLevels: Map<string, number> | undefined;
  /**
   * The pixels this screen has a level command in flight for, counted so overlapping gestures on the
   * same pixel release cleanly.
   *
   * The count is plain state and the signal beside it is what makes a release re-run the reconciliation
   * effect, so a pixel stops being protected the moment its command settles rather than at whatever
   * happens to arrive next.
   */
  const levelsInFlight = new Map<string, number>();
  const [levelsInFlightVersion, setLevelsInFlightVersion] = createSignal(0);

  function levelInFlight(key: string): boolean {
    return levelsInFlight.has(key);
  }

  /**
   * When each staged pixel stops being able to be confirmed — the deadline of the command that staged
   * it, as the relay clamped it.
   *
   * This is the bound the 2026-09-17 fix deliberately did without, and the residual it left behind. That
   * fix stopped the overlay being wiped wholesale whenever the room moved, which was right: an unrelated
   * revision arriving is not a statement about an operator's edit. But it also removed the only thing
   * that ever cleared a STALE optimistic value as a side effect, so a level whose command the relay gave
   * up on — and whose pixels the room then never published — stayed drawn for the rest of the session,
   * with the console saying nothing about it. "Keeps a change the room almost certainly made" was the
   * right trade over "silently reverts a change the room definitely made", but it is not a trade that
   * has to be permanent, and unbounded is not a lifetime.
   *
   * After the deadline nothing in the cloud can still deliver that command, so a pixel the room has not
   * confirmed by then is never going to be confirmed and the drawing goes back to the room's own value.
   * It happens ONCE per pixel, silently: the header has already said what it can (`clearControlFeedback`
   * on an unreported relay), and there is no new sentence here — the room reappearing is the statement.
   */
  const draftDeadlines = new Map<string, number>();
  const [draftExpiryVersion, setDraftExpiryVersion] = createSignal(0);
  let draftExpiryTimer: number | undefined;

  /**
   * Hold these pixels to the deadline of the command that staged them, and wake the reconciliation when
   * the earliest of them passes.
   *
   * One timer for the whole screen, re-armed at the earliest outstanding deadline rather than one timer
   * per pixel: a whole-room gesture is a few thousand pixels sharing one command and one deadline.
   */
  function expireDraft(keys: Iterable<string>, expiresAt: number): void {
    if (!Number.isFinite(expiresAt)) return;
    for (const key of keys) draftDeadlines.set(key, Math.max(draftDeadlines.get(key) ?? 0, expiresAt));
    armDraftExpiry();
  }

  function armDraftExpiry(): void {
    if (draftExpiryTimer != null) globalThis.clearTimeout(draftExpiryTimer);
    draftExpiryTimer = undefined;
    if (!draftDeadlines.size) return;
    const earliest = Math.min(...draftDeadlines.values());
    draftExpiryTimer = globalThis.setTimeout(() => {
      draftExpiryTimer = undefined;
      setDraftExpiryVersion((value) => value + 1);
    }, Math.max(0, earliest - Date.now())) as unknown as number;
  }

  onCleanup(() => {
    if (draftExpiryTimer != null) globalThis.clearTimeout(draftExpiryTimer);
  });

  /** Mark these pixels as having a command of ours in flight, and hand back the release. */
  function holdLevels(keys: Iterable<string>): () => void {
    const held = [...keys];
    for (const key of held) levelsInFlight.set(key, (levelsInFlight.get(key) ?? 0) + 1);
    setLevelsInFlightVersion((value) => value + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      for (const key of held) {
        const remaining = (levelsInFlight.get(key) ?? 1) - 1;
        if (remaining > 0) levelsInFlight.set(key, remaining);
        else levelsInFlight.delete(key);
      }
      setLevelsInFlightVersion((value) => value + 1);
    };
  }

  const fixtures = createMemo(() => venue.rig()?.fixtures.filter((fixture) => fixture.isControllable !== false) ?? []);
  const fixtureById = createMemo(() => new Map(fixtures().map((fixture) => [fixture.deviceId, fixture])));
  const liveById = createMemo(() => new Map((venue.controllerState()?.fixtures ?? []).map((fixture) => [fixture.deviceId, fixture])));
  const model = venue.roomModel;
  const authoritativeLevels = createMemo(() => {
    const value = model();
    const levels = new Map<string, number>();
    if (!value) return levels;
    const descriptors = fixtureById();
    const live = liveById();
    for (const element of value.elements) {
      const { deviceId } = element;
      if (!deviceId) {
        levels.set(element.key, 0);
        continue;
      }
      const recordId = rigRecordId('panel', [deviceId]);
      const saved = venue.rigRecordState?.(recordId);
      const intensity = saved?.state.values?.intensity;
      if (typeof intensity === 'number'
        && (saved!.revision === 0 || saved!.revision > (venue.rigAppliedRevision?.(recordId) ?? 0))) {
        levels.set(element.key, clampLevel(intensity));
        continue;
      }
      // A fixture the room has not reported has no level. It is left OUT, never drawn or recorded as 0:
      // `liveElementLevel` reads a missing fixture as 0, which is right only for a fixture that IS reported
      // and carries no intensity value.
      const fixture = live.get(deviceId);
      if (fixture) levels.set(element.key, liveElementLevel(fixture, descriptors.get(deviceId), element.elementIndex));
    }
    return levels;
  });
  /**
   * The look this screen edits: the authored levels, plus whatever is being dragged right now. It is
   * what the canvas draws whenever a controller is reporting, and it never leases a stream to do it.
   *
   * It used to start the controller's output preview by itself whenever something else owned the room,
   * with no time cap — a standing frame feed off the Pi for as long as the tab was open, over the cloud
   * when there was no cable. Owner ruling 2026-09-16: *"We set up a two-minute live preview specifically
   * so that we wouldn't blast data over the cloud all the time."* The two-minute control on Effects is
   * the only thing that starts a preview anywhere.
   */
  const shownLevels = createMemo(() => new Map([...authoritativeLevels(), ...draftLevels()]));

  /** The scene the room is on, as the controller last reported it. */
  const recalledSceneId = createMemo(() => String(
    venue.mirror()?.scenes?.recalledSceneId
      ?? venue.controllerState()?.scene?.recalledSceneId
      ?? venue.controllerState()?.scene?.sceneId
      ?? '',
  ));
  /**
   * Whether the controller has reported this room's levels — ALL of them.
   *
   * A console that reaches the cloud but not the controller has no `controllerState().fixtures` — no live
   * levels at all, because they come from the controller. An authored tree reports one fixture at a time,
   * seconds apart, so a room with some fixtures in and others still on their way is not reported either
   * (`venue.panelBaselineComplete`). Everything below depends on there being nothing authoritative to draw.
   */
  const roomReported = createMemo(() => venue.panelBaselineComplete());
  /**
   * Whether a level may be set or a scene applied here. Each records the room's current levels as its
   * Undo "before", so on a controller it waits for the room to be reported; a Micro's levels are its
   * records, which have no baseline to wait for.
   */
  const levelControlsEnabled = createMemo(() => venue.controlsEnabled() && (venue.microRecordWrites() || roomReported()));
  /**
   * With nothing reported, the drawing shows the scene the room is CURRENTLY ON.
   *
   * Scenes live in the room now rather than on one controller's disk, so this console can list the room's
   * scenes with no controller reached — and a drawing that showed a uniformly dark room beside that list
   * would contradict it. The record decoded here is the same one the controller recalls, so the preview
   * and the wire agree by construction; `decodeSceneLevels` is the one place that decode happens.
   * A controller that IS reporting always wins: nothing here runs while the room is on screen for real.
   */
  const recalledSceneLevels = createMemo(() => {
    if (roomReported()) return undefined;
    const scene = venue.sceneRecords().find((record) => record.sceneId === recalledSceneId());
    const decoded = decodeSceneLevels(scene, model(), venue.rig());
    return decoded.kind === 'valid' ? decoded.levels : undefined;
  });
  // The cloud listener already carries every record; a direct link carries list rows, so the one scene
  // being drawn is read on demand. `loadSceneRecord` is memoised per scene, so this settles at one read.
  createEffect(() => {
    const id = recalledSceneId();
    if (roomReported() || !id || venue.sceneRecords().some((record) => record.sceneId === id)) return;
    untrack(() => void venue.loadSceneRecord(id));
  });

  /** What the room canvas draws: the room's current scene when no controller has reported, else the look this screen edits. */
  const roomLevels = createMemo(() => {
    const scene = recalledSceneLevels();
    return scene ? new Map([...scene, ...draftLevels()]) : shownLevels();
  });
  const summary = createMemo(() => {
    const value = model();
    return value ? selectionSummary(selectedKeys(), value) : { pixels: 0, runs: [], headline: 'No pixels selected' };
  });
  const sceneSaveSummary = createMemo(() => {
    const value = model();
    if (!value) return { pixels: 0, runs: [], headline: 'No pixels selected' };
    const targets = selectedKeys().size
      ? selectedKeys()
      : new Set(value.elements.map((element) => element.key));
    return selectionSummary(targets, value);
  });
  const selectedLevelValues = createMemo(() => {
    const value = model();
    return value ? selectionLevels(selectedKeys(), value, shownLevels()) : [];
  });
  const distributionRows = createMemo(() => {
    const value = model();
    return value ? runDistributions(selectedKeys(), value, shownLevels()) : [];
  });
  const levelSummary = createMemo(() => summarizeLevels(selectedLevelValues()));
  const sliderLevel = createMemo(() => {
    const values = selectedLevelValues();
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
  });
  const selectedSupportsIntensity = createMemo(() => {
    const value = model();
    if (!value || !selectedKeys().size) return false;
    const ids = new Set(value.elements.filter((element) => selectedKeys().has(element.key)).map((element) => element.deviceId));
    return [...ids].every((id) => id !== undefined && fixtureIntensityKeys(fixtureById().get(id)).length > 0);
  });
  const collaborationLine = createMemo(() => {
    const activity = venue.activity();
    if (!activity || !['set_fixture_params', 'recall_scene'].includes(activity.command)) return '';
    const actor = activity.actor?.label || activity.actor?.uid || 'another operator';
    return activity.phase === 'editing' ? `${actor} is editing…` : `changed by ${actor} · just now`;
  });
  const sceneGroups = createMemo(() => groupScenesByFavorite(venue.scenes(), sceneQuery()));
  const historyState = createMemo(() => {
    historyVersion();
    return { canUndo: history.canUndo, canRedo: history.canRedo, length: history.length };
  });

  function roomKey(): string {
    return `${venue.selectedVenue()?.venueId ?? 'local'}:${venue.selectedRoom()?.roomId ?? venue.selectedDeviceId()}`;
  }

  createEffect(() => {
    const identity = roomKey();
    // A different room's confirmed levels are not a previous account of THIS room's, so the overlay
    // reconciliation starts again rather than diffing across the change.
    if (identity !== previousHistoryIdentity) previousAuthoritativeLevels = undefined;
    if (previousHistoryIdentity && identity !== previousHistoryIdentity) {
      clearPanelHistory(previousHistoryIdentity);
      setSelectionAnchor(undefined);
    }
    history = panelHistoryFor(identity);
    previousHistoryIdentity = identity;
    setHistoryVersion((value) => value + 1);
  });

  createEffect(() => {
    const value = model();
    const available = new Set(value?.elements.map((element) => element.key) ?? []);
    setSelectedKeys((current) => new Set([...current].filter((key) => available.has(key))));
    setDraftLevels((current) => new Map([...current].filter(([key]) => available.has(key))));
  });

  createEffect(() => {
    const session = gradient();
    if (gradientSessionForSelection(session, gradientSelectionId(selectedKeys())) !== session) closeGradientEditor();
  });

  /**
   * What the room says replaces the operator's overlay — for the pixels it SPOKE TO, and only those.
   *
   * There are exactly two reasons to drop a pixel from the draft, and both are about that one pixel:
   *
   * 1. **The room agrees.** The confirmed level has reached the staged one, so the overlay has nothing
   *    left to add. One byte of output quantization is about 0.0039; a wider tolerance used to erase a
   *    typed one-percent step before blur (3% → 4% differs by only .01), so that committed gesture never
   *    reached either the controller or history.
   * 2. **The room said something different about that pixel.** A scene Apply, or another operator's edit,
   *    moved it somewhere the operator did not put it. Daemon truth wins, and the overlay must get out of
   *    the way — otherwise the screen keeps drawing a level the room is not at.
   *
   * What is NOT a reason: the room moving at all. This used to wipe the WHOLE overlay whenever
   * `controllerState().stateRevision` advanced, and that is the "often doesn't stick" the owner reported
   * on 2026-09-17. The authored tree becomes canonical about a second after the Pi applies a change, via
   * a trigger that runs independently of the relay callable — while that callable spins for another 9–18
   * seconds. So an operator making a second edit a second after the first watched the FIRST edit's tree
   * node arrive and erase the SECOND edit, with nothing having failed at all. Blanket-clearing was only
   * ever safe while acknowledgements outran the operator, and that is exactly the assumption that broke.
   *
   * A pixel whose own command is still in flight is never dropped for reason 2. The room has not been
   * told about it yet, so "the room says something different" is not yet a statement about this edit —
   * it is the pre-edit value, and honouring it would revert a change that is on its way.
   *
   * Only state the console ADOPTED gets here: `authoritativeLevels()` is derived from
   * `controllerState()`, which moves when a delta was applied or a whole state installed and never on a
   * revision the console refused. That distinction is load-bearing. The wipe once keyed on the mirror's
   * `panelRevision`, which the publisher advances on every successful mirror write whether or not the
   * delta it carried was usable, so a delta the console REFUSED still destroyed the operator's pending
   * edit — the "flickered back on" the owner saw on 2026-09-15.
   */
  createEffect(() => {
    levelsInFlightVersion();
    draftExpiryVersion();
    const confirmed = authoritativeLevels();
    const previous = previousAuthoritativeLevels;
    previousAuthoritativeLevels = confirmed;
    const now = Date.now();
    setDraftLevels((current) => {
      if (!current.size) return current;
      const next = new Map(current);
      let changed = false;
      for (const [key, draft] of current) {
        const level = confirmed.get(key) ?? 0;
        const agrees = Math.abs(level - draft) <= LEVEL_TOLERANCE;
        const spoke = agrees || Boolean(
          previous && Math.abs(level - (previous.get(key) ?? 0)) > LEVEL_TOLERANCE && !levelInFlight(key),
        );
        // The third ground, and the only one that is about TIME rather than about the room: the command
        // that staged this pixel can no longer be delivered by the cloud, and the room never confirmed
        // it. See `draftDeadlines`.
        const expired = (draftDeadlines.get(key) ?? Infinity) <= now;
        if (!spoke && !expired) continue;
        next.delete(key);
        changed = true;
      }
      return changed ? next : current;
    });
    // A pixel that has left the overlay has no overlay left to expire. Untracked deliberately: this
    // effect reconciles the draft against the ROOM, and making it depend on the draft would re-enter it
    // on every staged gesture for no reconciliation it has not already done.
    untrack(() => {
      for (const key of draftDeadlines.keys()) {
        if (!draftLevels().has(key)) draftDeadlines.delete(key);
      }
    });
    armDraftExpiry();
  });

  let roomCanvas: RoomCanvasHandle | undefined;

  function resetView(): void {
    roomCanvas?.resetView();
  }

  function blockAt(key: string, value: PanelModel): Set<string> {
    // Clicking a lit pixel selects the contiguous lit run it belongs to, from the levels the operator can
    // SEE. There is one path for every transport now: the cloud carries the room's real per-element state,
    // so a hosted console reads the same levels a direct link does. (It used to index the mirror's coalesced
    // lit spans instead, which is why a click grabbed whatever the averaged projection called one island.)
    return litBlock(key, value, roomLevels());
  }

  function chooseShortcut(clicked: RunClickTarget, modifiers: RunClickModifiers): void {
    const value = model();
    if (!value) return;
    const listedRuns = value.zones.flatMap((zone) => zone.runs);
    const result = applyRunClick(selectedKeys(), selectionAnchor(), listedRuns, clicked, modifiers);
    setSelectedKeys(result.selection);
    setSelectionAnchor(result.anchor);
  }

  // ── level writes ──────────────────────────────────────────────────────────────────────────────────

  /** The known levels of `keys`. A pixel with no reported level is left out rather than recorded as 0. */
  function snapshot(keys: Iterable<string>, source: ReadonlyMap<string, number> = shownLevels()): Map<string, number> {
    return new Map([...keys].flatMap((key) => {
      const level = source.get(key);
      return level === undefined ? [] : [[key, clampLevel(level)] as const];
    }));
  }

  function stageDraft(levels: ReadonlyMap<string, number>): void {
    setDraftLevels((current) => new Map([...current, ...levels]));
  }

  function clearDraft(keys: Iterable<string>): void {
    setDraftLevels((current) => {
      const next = new Map(current);
      for (const key of keys) next.delete(key);
      return next;
    });
  }

  /**
   * The command payloads for a level map.
   *
   * A VARYING look — a gradient, an applied scene, an undo — is ONE atomic
   * `set_fixture_params` carrying `elementValues`, because the cloud relay coalesces gesture commands
   * keep-last and a series of chunks silently loses all but the last. A uniform set can use the
   * cheaper `elements` + `values` form, which is one command too.
   *
   * Every dimmer-class channel of an element carries the same value (`fixtureIntensityKeys`): the tape
   * has two parallel dimmer channels and writing only the first leaves the second stale.
   */
  function commandPayloads(levels: ReadonlyMap<string, number>): Array<Record<string, unknown>> {
    const value = model();
    if (!value) return [];
    const descriptors = fixtureById();
    const batches = groupElementLevelBatches(levels, value);
    if (batches.length === 1) {
      const batch = batches[0];
      const payloads: Array<Record<string, unknown>> = [];
      const byChannels = new Map<string, Record<string, number[]>>();
      for (const [deviceId, indices] of Object.entries(batch.elements)) {
        const keys = fixtureIntensityKeys(descriptors.get(deviceId));
        if (!keys.length) continue;
        const signature = keys.join('|');
        byChannels.set(signature, { ...(byChannels.get(signature) ?? {}), [deviceId]: indices });
      }
      for (const [signature, elements] of byChannels) {
        const values = Object.fromEntries(signature.split('|').map((key) => [key, batch.level]));
        payloads.push({ elements, values, projection: 'delta-v1' });
      }
      return payloads;
    }
    const elementValues: Record<string, Record<string, Record<string, number>>> = {};
    for (const [elementKey, level] of levels) {
      const element = value.byKey.get(elementKey);
      const deviceId = element?.deviceId;
      if (!deviceId) continue;
      const keys = fixtureIntensityKeys(descriptors.get(deviceId));
      if (!keys.length) continue;
      elementValues[deviceId] ??= {};
      elementValues[deviceId][String(element.elementIndex)] = Object.fromEntries(
        keys.map((key) => [key, clampLevel(level)]),
      );
    }
    return Object.keys(elementValues).length ? [{ elementValues, projection: 'delta-v1' }] : [];
  }

  /**
   * Send a level map, and say whether it went.
   *
   * Every payload in one commit targets a DISJOINT set of fixtures — `commandPayloads` splits a uniform
   * set by channel signature, and each device falls under exactly one signature — so they need no order
   * against each other and go together. `sent` fires once they are all on their way, which is all the
   * next commit waits for.
   */
  async function dispatchLevelsNow(
    levels: ReadonlyMap<string, number>,
    sent: () => void,
  ): Promise<boolean> {
    const payloads = commandPayloads(levels);
    // Never send a command with an empty target set — the daemon rightly refuses it.
    if (!payloads.length) {
      sent();
      return false;
    }
    const calls = payloads.map((params) => venue.sendPanelEdit(params));
    sent();
    // Settled, not `all`: a second failing payload must not surface as an unhandled rejection while the
    // first one is being reported.
    const outcomes = await Promise.allSettled(calls);
    const refused = outcomes.find((outcome) => outcome.status === 'rejected');
    if (refused) throw refused.reason;
    // Hold the staged pixels only as long as the cloud can still deliver the command that staged them.
    // The LAST deadline across the payloads governs: a commit that split by fixture channel signature is
    // one operator action, and half of it expiring while the other half stands would draw a room that
    // never existed.
    const deadline = Math.max(...outcomes.map((outcome) => (
      outcome.status === 'fulfilled' && Number.isFinite(outcome.value?.expiresAt)
        ? Number(outcome.value.expiresAt)
        : 0
    )));
    if (deadline > 0) expireDraft(levels.keys(), deadline);
    return true;
  }

  /**
   * Level commits leave in the order they were made, and the next one waits only for the previous one to
   * have been SENT — never for the room to have confirmed it.
   *
   * This used to chain on the whole awaited round-trip. On prod, 2026-09-17, that round-trip was taking
   * 9–15 s while the Pi applied each change in about 17 ms, so an operator's second and third attempts
   * queued up behind a call that had long since stopped meaning anything instead of overtaking it.
   */
  function dispatchLevels(levels: ReadonlyMap<string, number>): Promise<boolean> {
    const immutable = new Map(levels);
    const releaseHold = holdLevels(immutable.keys());
    let release!: () => void;
    const sent = new Promise<void>((resolve) => {
      release = resolve;
    });
    const previous = levelDispatchTail;
    levelDispatchTail = sent;
    // `finally` as well as the explicit call: a dispatch that throws before sending must not strand the
    // tail and wedge every later commit behind it.
    return previous.catch(() => undefined)
      .then(() => dispatchLevelsNow(immutable, release))
      .finally(() => {
        release();
        releaseHold();
      });
  }

  async function commitChange(
    label: string,
    after: Map<string, number>,
    before = snapshot(after.keys()),
    success?: string,
    mergeKey?: string,
  ): Promise<boolean> {
    const changed = changedLevels(before, after);
    if (!changed.after.size || mapEquals(changed.before, changed.after)) return false;
    // Absolute commits deliberately send the complete affected set, including values the
    // local feed currently believes are unchanged. A reconnecting cloud mirror may lag the daemon;
    // omitting an apparent no-op would otherwise leave that pixel at a stale physical level.
    stageDraft(after);
    const result = await venue.runAction(`pixels-${label.toLowerCase().replace(/\W+/g, '-')}`, () => dispatchLevels(after), success);
    // A FAILED dispatch is now the only thing that takes the operator's levels back off the screen, and
    // it means one of two things: the room refused the command, or the command never left this browser.
    // It no longer means the cloud relay was slow — `controllerCall` resolves a level write on the room's
    // own authored tree and reports an unconfirmed relay as "not yet", not as "not applied". Reverting on
    // a relay timeout is exactly what made the owner watch a change the room had already made disappear
    // (prod, 2026-09-17: applied on the Pi at 15:25:12.167, reverted here at 15:25:21).
    if (!result) {
      clearDraft(after.keys());
      void venue.refreshController().catch(() => undefined);
      return false;
    }
    history.push({ label, before: changed.before, after: changed.after, mergeKey });
    setHistoryVersion((value) => value + 1);
    return true;
  }

  function beginAbsolute(): void {
    if (!absoluteBefore) absoluteBefore = snapshot(selectedKeys());
  }

  function uniformMap(value: number): Map<string, number> {
    const level = clampLevel(value);
    return new Map([...selectedKeys()].map((key) => [key, level]));
  }

  function sendUniformGesture(value: number, final: boolean, onApplied?: () => void): void {
    const levels = uniformMap(value);
    if (!levels.size) return;
    stageDraft(levels);
    const payloads = commandPayloads(levels);
    // Nothing to send is not something to wait for. A hold taken here would never be released — no
    // payload means no acknowledgement to release it on — and these pixels would then be exempt from the
    // "the room moved them" reconciliation for the rest of the session.
    if (!payloads.length) return;
    const ordered = [...selectedKeys()];
    const fingerprint = `${ordered.length}:${ordered[0] ?? ''}:${ordered.at(-1) ?? ''}`;
    // Only a RELEASE is held. An editing sample is keep-latest and its value is superseded rather than
    // confirmed, so holding one would protect a pixel indefinitely through a long drag.
    const releaseHold = final ? holdLevels(levels.keys()) : undefined;
    let pending = payloads.length;
    let refused = false;
    // Every payload settles one way or the other, so the hold is always released. A REFUSAL is the one
    // verdict that takes the operator's levels back off the screen — the same rule `commitChange` keeps,
    // and it has to be stated here too: with the overlay no longer wiped wholesale on any revision, a
    // refused release would otherwise leave the screen drawing a level the room never took.
    let deadline = 0;
    const settle = (outcome?: unknown): void => {
      const expiresAt = (outcome as RoomConfirmation | undefined)?.expiresAt;
      if (Number.isFinite(expiresAt)) deadline = Math.max(deadline, Number(expiresAt));
      pending -= 1;
      if (pending > 0) return;
      releaseHold?.();
      if (refused) clearDraft(levels.keys());
      else {
        // The release landed, or nobody could say. Either way the staged pixels are held only until the
        // cloud stops being able to deliver the command — after that an unconfirmed one is never going
        // to be confirmed and the drawing goes back to the room.
        if (deadline > 0) expireDraft(levels.keys(), deadline);
        onApplied?.();
      }
    };
    const refuse = (): void => {
      refused = true;
      settle();
    };
    payloads.forEach((params, index) => venue.sendPanelGesture(
      params,
      `pixels:${fingerprint}:${index}`,
      final,
      final ? settle : undefined,
      final ? refuse : undefined,
    ));
  }

  function previewAbsolute(value: number): void {
    beginAbsolute();
    if (gradient().open) closeGradientEditor();
    sendUniformGesture(value, false);
  }

  function previewNumericAbsolute(value: number): void {
    beginAbsolute();
    if (gradient().open) closeGradientEditor();
    stageDraft(uniformMap(value));
  }

  function finishAbsolute(value: number, label = 'Set intensity'): void {
    beginAbsolute();
    const after = uniformMap(value);
    if (!after.size) {
      absoluteBefore = undefined;
      return;
    }
    if (gradient().open) closeGradientEditor();
    const changed = changedLevels(absoluteBefore ?? snapshot(after.keys()), after);
    const targetHistory = history;
    sendUniformGesture(value, true, changed.after.size
      ? () => {
        targetHistory.push({ label, before: changed.before, after: changed.after });
        setHistoryVersion((current) => current + 1);
      }
      : undefined);
    absoluteBefore = undefined;
  }

  function setPreset(value: number): void {
    beginAbsolute();
    finishAbsolute(value, `Set ${Math.round(value * 100)}%`);
  }

  function gradientMap(stops: readonly GradientStop[], mirror: boolean): Map<string, number> {
    const value = model();
    if (!value) return new Map();
    return mapLevelsToSelection(selectedKeys(), value, gradientLevels(stops, selectedKeys().size, mirror));
  }

  function toggleGradient(): void {
    const next = toggleGradientSession(gradient(), gradientSelectionId(selectedKeys()));
    if (!next.open) {
      closeGradientEditor();
      return;
    }
    gradientBefore = snapshot(selectedKeys());
    setGradientStops(GRADIENT_PRESETS['fade-in'].map((stop) => ({ ...stop })));
    setGradientMirror(false);
    setGradient(next);
  }

  function closeGradientEditor(): void {
    if (gradientBefore) clearDraft(gradientBefore.keys());
    setGradient(CLOSED_GRADIENT);
    gradientBefore = undefined;
  }

  function previewGradient(stops: GradientStop[], mirror: boolean): void {
    if (!gradientBefore) gradientBefore = snapshot(selectedKeys());
    setGradientStops(stops);
    setGradientMirror(mirror);
    stageDraft(gradientMap(stops, mirror));
  }

  async function commitGradient(stops: GradientStop[], mirror: boolean): Promise<void> {
    setGradientStops(stops);
    setGradientMirror(mirror);
    const after = gradientMap(stops, mirror);
    const before = gradientBefore ?? snapshot(after.keys());
    const committed = await commitChange('Apply gradient', after, before, 'Gradient applied');
    if (committed) {
      // The section stays open on this selection so the same gradient can be adjusted and sent again.
      // The levels just applied are the new baseline, so dropping the pre-gradient snapshot is what
      // makes a later close revert to them rather than back past a change that is already committed.
      setGradient(gradientApplied(gradient()));
      gradientBefore = undefined;
    }
  }

  /**
   * Rolled back only on a refusal or a command that never left — see `commitChange` for why.
   *
   * A second press while the first is still in flight is not a second undo, and it must not get as far
   * as touching anything. `runAction` refuses a key it is already running by returning `undefined`,
   * which is indistinguishable here from a refusal — so the old code popped history, staged the levels,
   * discovered it had been refused a turn, and then "rolled back" by clearing draft keys belonging to
   * the press that was still in flight. Every button now disables on its own action key, so the UI
   * cannot produce this; ⌘Z bypasses the buttons, so the keyboard has to say it itself.
   */
  async function undo(): Promise<void> {
    if (venue.actionBusy() === 'pixel-undo') return;
    const change = history.undo();
    if (!change) return;
    setHistoryVersion((value) => value + 1);
    stageDraft(change.before);
    const result = await venue.runAction('pixel-undo', () => dispatchLevels(change.before), `Undid ${change.label.toLowerCase()}`);
    if (!result) {
      history.redo();
      clearDraft(change.before.keys());
      setHistoryVersion((value) => value + 1);
    }
  }

  /** As `undo`, in the other direction, and it declines a second press the same way. */
  async function redo(): Promise<void> {
    if (venue.actionBusy() === 'pixel-redo') return;
    const change = history.redo();
    if (!change) return;
    setHistoryVersion((value) => value + 1);
    stageDraft(change.after);
    const result = await venue.runAction('pixel-redo', () => dispatchLevels(change.after), `Redid ${change.label.toLowerCase()}`);
    if (!result) {
      history.undo();
      clearDraft(change.after.keys());
      setHistoryVersion((value) => value + 1);
    }
  }

  /** Typing somewhere: the room's own keys (undo, the space-bar pan) must not steal the keystroke. */
  function typingInto(target: EventTarget | null): boolean {
    return Boolean((target as HTMLElement | null)?.matches('input:not([type="range"]), textarea, select, [contenteditable="true"]'));
  }

  function keyDown(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
    if (typingInto(event.target)) return;
    event.preventDefault();
    if (event.shiftKey) void redo();
    else void undo();
  }

  window.addEventListener('keydown', keyDown);

  /** The Pi captures its look; a bound Micro saves the printed rig scene state. */
  async function saveScene(): Promise<void> {
    const name = sceneName().trim();
    const value = model();
    const micro = venue.microRecordWrites();
    if ((!micro && !name) || !value) return;
    const targets = selectionForSceneSave(selectedKeys());
    const result = await venue.runAction('save-scene', async () => {
      if (!micro) return venue.controllerCall('save_scene', {
        scope: 'room', name,
        ...(targets ? { elements: toElementTargets(targets, value) } : {}),
      });
      const sceneId = crypto.randomUUID();
      const keys = targets ?? new Set(value.elements.map((element) => element.key));
      const levels = authoritativeLevels();
      const byFixture = new Map<string, number[]>();
      for (const element of value.elements) {
        if (!keys.has(element.key) || !element.deviceId) continue;
        const values = byFixture.get(element.deviceId) ?? [];
        values.push(clampLevel(levels.get(element.key) ?? 0));
        byFixture.set(element.deviceId, values);
      }
      const fixtures = [...byFixture].map(([fixtureId, levels]) => ({ fixtureId,
        channels: { intensity: levels.reduce((total, level) => total + level, 0) / levels.length } }));
      await venue.writeRigRecord('scene', [sceneId], { op: 'replace', value: { fixtures } });
      return true;
    }, micro ? undefined : `Saved ${name}`);
    if (!result) return;
    setSceneModal(false);
    setSceneName('');
  }

  function beginRenameScene(scene: SceneSummary): void {
    setSceneMenuId('');
    setRenamingSceneId(sceneId(scene));
    setRenameSceneName(scene.name);
  }

  async function toggleControllerSceneFavorite(scene: SceneSummary): Promise<void> {
    const id = sceneId(scene);
    if (!id) return;
    setSceneMenuId('');
    const favorite = scene.favorite !== true;
    await venue.runAction(
      `favorite-scene-${id}`,
      () => venue.controllerCall('set_scene_favorite', { sceneId: id, favorite }),
      favorite ? `Added ${scene.name} to favorites` : `Removed ${scene.name} from favorites`,
    );
  }

  async function renameControllerScene(scene: SceneSummary): Promise<void> {
    const id = sceneId(scene);
    const name = renameSceneName().trim();
    if (!id || !name || name === scene.name) {
      setRenamingSceneId('');
      return;
    }
    const result = await venue.runAction(`rename-scene-${id}`, () => venue.controllerCall('rename_scene', {
      scope: 'room',
      sceneId: id,
      name,
    }), `Renamed scene to ${name}`);
    if (!result) return;
    setRenamingSceneId('');
  }

  async function deleteControllerScene(): Promise<void> {
    const scene = deletingScene();
    const id = scene ? sceneId(scene) : '';
    if (!scene || !id) return;
    const result = await venue.runAction(`delete-scene-${id}`, () => !legacySceneCommands()
      ? venue.writeRigRecord('scene', [id], { op: 'delete' })
      : venue.controllerCall('delete_scene', { scope: 'room', sceneId: id }), `Deleted ${scene.name}`);
    if (!result) return;
    setDeletingScene(undefined);
    setSceneMenuId('');
  }

  async function recallControllerScene(scene: SceneSummary): Promise<void> {
    const value = model();
    const id = sceneId(scene);
    if (!value || !id) return;
    if (!await venue.confirmTakeover({
      source: 'panel',
      action: sceneApplyTakeoverAction(scene.name),
    })) return;
    const before = snapshot(value.elements.map((element) => element.key), authoritativeLevels());
    const result = await venue.runAction(`scene-${id}`, async () => {
      if (!venue.microRecordWrites()) return venue.controllerCall('recall_scene', {
        scope: 'room', sceneId: id, takeOver: true, projection: 'delta-v1',
      });
      const fixtures = venue.rigRecordState(rigRecordId('scene', [id]))?.state.fixtures;
      if (!Array.isArray(fixtures)) throw new Error('Change not applied.');
      return Promise.all(fixtures.map((fixture: { fixtureId: string; channels: { intensity: number } }) =>
        venue.writeRigRecord('panel', [fixture.fixtureId], {
          op: 'set', set: [{ path: 'values/intensity', value: fixture.channels.intensity }],
        })));
    }, sceneAppliedNotice(scene.name));
    if (!result) return;
    const after = snapshot(value.elements.map((element) => element.key), authoritativeLevels());
    const changed = changedLevels(before, after);
    if (changed.after.size) {
      history.push({ label: `Apply ${scene.name}`, before: changed.before, after: changed.after });
      setHistoryVersion((current) => current + 1);
    }
  }

  async function takeOverRoom(): Promise<void> {
    if (!await venue.confirmTakeover({
      source: 'panel',
      action: 'puts the room on the Scenes page look',
    })) return;
    await venue.runAction(
      'take-over-scenes',
      () => venue.controllerCall('set_mode', { source: 'panel', projection: 'delta-v1' }),
      'Scenes are live',
    );
  }

  function renderSceneCard(scene: SceneSummary): JSX.Element {
    const id = () => sceneId(scene);
    const applied = () => recalledSceneId() === id();
    const copy = () => sceneCardCopy(applied());
    const favorite = () => scene.favorite === true;
    return (
      <article
        class="scene-card"
        classList={{ current: applied(), favorite: favorite() }}
        data-scene-id={id()}
        data-current={applied() ? 'true' : 'false'}
        data-favorite={favorite() ? 'true' : 'false'}
      >
        <Show
          when={renamingSceneId() === id()}
          fallback={(
            <div class="scene-card-name">
              <strong>{scene.name}</strong>
              <small>{copy().status}</small>
            </div>
          )}
        >
          <form class="scene-rename" onSubmit={(event) => { event.preventDefault(); void renameControllerScene(scene); }}>
            <input value={renameSceneName()} onInput={(event) => setRenameSceneName(event.currentTarget.value)} aria-label={`Rename ${scene.name}`} autofocus />
            <BusyButton busyKey={`rename-scene-${id()}`} type="submit" class="text-button" disabled={!renameSceneName().trim()}>Save</BusyButton>
            <button type="button" class="text-button" onClick={() => setRenamingSceneId('')}>Cancel</button>
          </form>
        </Show>
        <Show when={legacySceneCommands() && !venue.rigUnassigned()}>
          <BusyButton
            busyKey={`favorite-scene-${id()}`}
            type="button"
            class="scene-favorite-toggle"
            classList={{ favorite: favorite() }}
            aria-label={favorite() ? `Remove ${scene.name} from favorites` : `Add ${scene.name} to favorites`}
            aria-pressed={favorite()}
            disabled={!venue.controlsEnabled()}
            onClick={() => void toggleControllerSceneFavorite(scene)}
          ><Star size={17} fill={favorite() ? 'currentColor' : 'none'} /></BusyButton>
          <BusyButton
            busyKey={`scene-${id()}`}
            class="button button-secondary scene-apply"
            disabled={!levelControlsEnabled()}
            onClick={() => void recallControllerScene(scene)}
          ><Check size={15} /> {copy().action}</BusyButton>
        </Show>
        <Show when={!venue.rigUnassigned() && !legacySceneCommands() && venue.rigRecordState(rigRecordId('scene', [id()]))}>
          <BusyButton busyKey={`scene-${id()}`} class="button button-secondary scene-apply"
            disabled={!sceneEditEnabled()} onClick={() => void recallControllerScene(scene)}>
            <Check size={15} /> {copy().action}
          </BusyButton>
        </Show>
        <Show when={!venue.rigUnassigned() && (legacySceneCommands() || venue.rigRecordState(rigRecordId('scene', [id()])))}>
          <div class="scene-menu">
            <button
              class="icon-button"
              aria-label={`More actions for ${scene.name}`}
              aria-expanded={sceneMenuId() === id()}
              disabled={!legacySceneCommands() && !sceneEditEnabled()}
              onClick={() => setSceneMenuId((current) => current === id() ? '' : id())}
            ><MoreHorizontal size={18} /></button>
            <Show when={sceneMenuId() === id()}>
              <div class="scene-menu-popover" role="menu">
                <Show when={legacySceneCommands()}>
                  <button role="menuitem" onClick={() => void toggleControllerSceneFavorite(scene)}>
                    {favorite() ? 'Remove from favorites' : 'Add to favorites'}
                  </button>
                  <button role="menuitem" onClick={() => beginRenameScene(scene)}>Rename</button>
                </Show>
                <button role="menuitem" class="danger-text" onClick={() => { setDeletingScene(scene); setSceneMenuId(''); }}>Delete</button>
              </div>
            </Show>
          </div>
        </Show>
      </article>
    );
  }

  onCleanup(() => {
    window.removeEventListener('keydown', keyDown);
  });

  const selectionControls = (
    <>
      <section class="surface selection-control" data-testid={TEST_IDS.selectionControl}>
        <div class="selection-summary">
          <span><Crosshair size={20} /></span>
          <div>
            <small>Current selection</small>
            <h2>{summary().headline}</h2>
            <Show when={summary().runs.length} fallback={<p>Drag across the room to select pixels, or choose a run.</p>}>
              <p class="selection-run-names">{summary().runs.map((run) => run.name).join(' · ')}</p>
            </Show>
            <Show when={collaborationLine()}><p class="selection-attribution" data-testid={TEST_IDS.selectionAttribution}>{collaborationLine()}</p></Show>
          </div>
        </div>
      </section>
      <Show when={venue.canControl()}>
        <div class="surface selection-controls">
          <div class="history-controls" aria-label="Level history" data-history-length={historyState().length}>
            <BusyButton busyKey="pixel-undo" disabled={!venue.controlsEnabled() || !historyState().canUndo} onClick={() => void undo()} title="Undo level change"><Undo2 size={16} /> Undo</BusyButton>
            <BusyButton busyKey="pixel-redo" disabled={!venue.controlsEnabled() || !historyState().canRedo} onClick={() => void redo()} title="Redo level change"><Redo2 size={16} /> Redo</BusyButton>
          </div>
          <Show when={selectedKeys().size > 0}>
            <Show when={selectedSupportsIntensity()}>
              <div class="selection-intensity">
                {/* The fader's own value already moved; the slot answers for the commit behind it. */}
                <span><strong>Intensity</strong><output data-testid={TEST_IDS.selectionLevelSummary}><BusySlot busy={venue.actionBusy().startsWith('pixels-')} />{levelSummary().label}</output></span>
                <label><span>Level</span><span><input type="number" min="0" max="100" step="1" value={Math.round(sliderLevel() * 100)} disabled={!levelControlsEnabled()} onFocus={beginAbsolute} onInput={(event) => previewNumericAbsolute(event.currentTarget.valueAsNumber / 100)} onBlur={(event) => finishAbsolute(event.currentTarget.valueAsNumber / 100)} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} aria-label="Selection intensity percent" /><i>%</i></span></label>
                <div class="vertical-intensity-control">
                  <label class="vertical-fader">
                    <span class="sr-only">Selection intensity</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={sliderLevel()}
                      disabled={!levelControlsEnabled()}
                      onPointerDown={beginAbsolute}
                      onFocus={beginAbsolute}
                      onInput={(event) => previewAbsolute(event.currentTarget.valueAsNumber)}
                      onChange={(event) => finishAbsolute(event.currentTarget.valueAsNumber)}
                      aria-label="Selection intensity"
                      data-testid={TEST_IDS.fixtureIntensity}
                    />
                  </label>
                  <div class="intensity-presets" aria-label="Intensity presets">
                    <For each={[1, .5, 0]}>{(value) => <button disabled={!levelControlsEnabled()} onClick={() => setPreset(value)}>{Math.round(value * 100)}%</button>}</For>
                  </div>
                </div>
              </div>
              <div class="gradient-entry">
                <button class="button button-secondary" disabled={!levelControlsEnabled()} onClick={toggleGradient} data-testid={TEST_IDS.gradientToggle}>
                  {gradientEntryLabel(gradient())}
                </button>
              </div>
              <Show when={gradient().open}>
                <GradientEditor
                  stops={gradientStops()}
                  mirror={gradientMirror()}
                  disabled={!levelControlsEnabled()}
                  rows={distributionRows()}
                  applied={gradient().applied}
                  onPreview={previewGradient}
                  onApply={(stops, mirror) => void commitGradient(stops, mirror)}
                  busy={venue.actionBusy() === 'pixels-apply-gradient'}
                />
              </Show>
            </Show>
          </Show>
          <Show when={!venue.rigUnassigned()}>
            <div class="button-row selection-actions">
              <button class="button button-primary" disabled={!sceneEditEnabled()}
                onClick={() => venue.microRecordWrites() ? void saveScene() : setSceneModal(true)}><Save size={16} /> Save scene</button>
            </div>
          </Show>
        </div>
      </Show>
    </>
  );

  const runsPanel = (
    <CardSection
      class="fixture-selection-aid run-selection-aid"
      bodyClass="run-selection-list"
      heading={<div class="section-heading"><div><h2>Runs</h2></div></div>}
    >
        <For each={model()?.zones ?? []}>
          {(zone) => (
            <div class="run-zone">
              <button class="run-zone-heading" onClick={(event) => chooseShortcut(zone.runs.map((run) => run.id), event)}>
                <span>{zone.name}</span><small>{zone.runs.length} run{zone.runs.length === 1 ? '' : 's'}</small>
              </button>
              <For each={zone.runs}>
                {(run) => {
                  const selectedCount = () => run.elements.filter((element) => selectedKeys().has(element.key)).length;
                  return (
                    <button
                      class="run-row"
                      classList={{ selected: selectedCount() === run.elements.length, partial: selectedCount() > 0 && selectedCount() < run.elements.length }}
                      aria-pressed={selectedCount() === run.elements.length}
                      onClick={(event) => chooseShortcut(run.id, event)}
                      data-run-id={run.id}
                      title={run.wiringNote || run.name}
                    >
                      <span class="run-line" />
                      <span><strong>{run.name}</strong><small>{run.elements.length.toLocaleString()} pixels{selectedCount() ? ` · ${selectedCount().toLocaleString()} selected` : ''}</small></span>
                    </button>
                  );
                }}
              </For>
            </div>
          )}
        </For>
    </CardSection>
  );

  const sceneLibrary = (
    <CardSection
      class="scenes-surface"
      bodyClass="scene-library-scroll"
      testId={TEST_IDS.sceneLibrary}
      bodyTestId={TEST_IDS.sceneLibraryScroll}
      heading={<div class="section-heading"><div><h2>Venue scenes</h2></div></div>}
      tools={(
        <label class="library-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={sceneQuery()}
            onInput={(event) => setSceneQuery(event.currentTarget.value)}
            placeholder="Search scenes"
            aria-label="Search scenes"
          />
        </label>
      )}
      footer={(
        <Show when={venue.scenes().length > 0 && !sceneGroups().favorites.length && !sceneGroups().all.length}>
          <p class="quiet-empty card-section-note">No scenes match “{sceneQuery().trim()}”.</p>
        </Show>
      )}
    >
      <Show when={venue.scenes().length > 0} fallback={<p class="quiet-empty">No scenes saved yet. Select pixels, set a look, then save it to apply later.</p>}>
          <Show when={sceneGroups().favorites.length}>
            <section class="scene-favorite-group" data-scene-group="favorites">
              <div class="scene-group-heading"><Star size={15} fill="currentColor" /><h3>Favorites</h3></div>
              <div class="scene-grid"><For each={sceneGroups().favorites}>{renderSceneCard}</For></div>
            </section>
          </Show>
          <Show when={sceneGroups().all.length}>
            <section class="scene-all-group" data-scene-group="all">
              <div class="scene-group-heading"><h3>All scenes</h3></div>
              <div class="scene-grid"><For each={sceneGroups().all}>{renderSceneCard}</For></div>
            </section>
          </Show>
      </Show>
    </CardSection>
  );

  return (
    <>
      <ScreenHeader
        title="Scenes"
        description="Drag to select pixels."
        actions={(
          <Show when={!venue.rigUnassigned() && venue.activeSource() !== 'panel' && !venue.controlOwnership()}>
            <BusyButton
              busyKey="take-over-scenes"
              class="button button-primary"
              disabled={!venue.controlAvailable()}
              onClick={() => void takeOverRoom()}
              data-testid={TEST_IDS.takeOverScenes}
            >Take over from {venue.activeSourceLabel()}</BusyButton>
          </Show>
        )}
      />

      <Show when={!venue.canControl() && !venue.rigUnassigned()}>
        <InlineNotice tone="warning" title="View only">
          {venue.localOrigin ? 'Pair this browser with the controller to use room controls.' : undefined}
        </InlineNotice>
      </Show>

      {sceneLibrary}

      <Show when={layoutWarning()}><InlineNotice tone="warning">{layoutWarning()}</InlineNotice></Show>

      <Show
        when={model()?.elements.length && fixtures().length}
        fallback={<Show when={!layoutWarning()}><EmptyState icon={<Lightbulb size={24} />} title={layoutLoading() ? 'Loading the room' : 'Choose a controller'} detail="Choose a room and controller to load its tape drawing and live levels." /></Show>}
      >
        <Show when={venue.activeSource() !== 'panel' && !venue.controlOwnership()}>
          <InlineNotice tone="warning">
            <span data-testid={TEST_IDS.stagedEditsNotice}>{venue.activeSourceLabel()} is controlling the room — your edits are staged until you take over.</span>
          </InlineNotice>
        </Show>
        <div class="panel-console">
          <aside class="panel-column panel-column-selection">{selectionControls}</aside>

          <section class="surface panel-room-surface panel-column-room">
            <div class="panel-room-toolbar">
              <div class="panel-room-live">
                <span class={`status-dot ${roomReported() ? 'online' : ''}`} />
                <span><strong>{roomReported() ? 'Live room' : 'Waiting for live state'}</strong></span>
              </div>
              <div class="panel-room-tools">
                <Show when={selectedKeys().size}><span data-testid={TEST_IDS.selectionCount}><strong>{selectedKeys().size.toLocaleString()}</strong> selected</span></Show>
                <button class="text-button" onClick={() => setSelectedKeys(new Set(model()?.elements.map((element) => element.key) ?? []))}>Select all</button>
                <button class="text-button" disabled={!selectedKeys().size} onClick={() => setSelectedKeys(new Set())}>None</button>
                <button class="text-button reset-view" onClick={resetView}><Maximize2 size={14} /> Reset view</button>
              </div>
            </div>
            <RoomCanvas
              class="panel-room-canvas"
              model={model()}
              levels={roomLevels()}
              fixtures={liveById()}
              interactive
              selectedKeys={selectedKeys()}
              onSelectionChange={(selection) => setSelectedKeys(selection)}
              blockAt={blockAt}
              storageKey={'nuance-venue:room-view-2d:' + roomKey()}
              apiRef={(api) => { roomCanvas = api; }}
              mirrorPanelRevision={venue.mirror()?.panelRevision ?? ''}
            />
          </section>

          <aside class="panel-column panel-column-runs">
            <button class="runs-disclosure" onClick={() => setRunsOpen((value) => !value)} aria-expanded={runsOpen()}>
              {runsOpen() ? 'Hide runs' : 'Runs'}
            </button>
            <div class="panel-column-runs-body" classList={{ open: runsOpen() }}>{runsPanel}</div>
          </aside>
        </div>
      </Show>

      <Modal title="Save this look" open={sceneModal() && legacySceneCommands()} onClose={() => setSceneModal(false)}>
        <div class="simple-form">
          <Show when={legacySceneCommands()}>
            <p>Save the controller-confirmed levels for {sceneSaveSummary().headline}.</p>
            <label><span>Scene name</span><input autofocus value={sceneName()} onInput={(event) => setSceneName(event.currentTarget.value)} placeholder="Evening welcome" /></label>
          </Show>
          <BusyButton busyKey="save-scene" class="button button-primary button-large" disabled={!sceneEditEnabled() || (legacySceneCommands() && !sceneName().trim())} onClick={() => void saveScene()}><Save size={17} /> Save scene</BusyButton>
        </div>
      </Modal>
      <ConfirmModal
        open={Boolean(deletingScene())}
        title="Delete scene?"
        detail={`Delete “${deletingScene()?.name ?? 'this scene'}”? This cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
        busyKey={`delete-scene-${sceneId(deletingScene() ?? ({} as SceneSummary))}`}
        onConfirm={() => void deleteControllerScene()}
        onCancel={() => setDeletingScene(undefined)}
      />
    </>
  );
}
